// src/components/Business/Scheduling/MoversJobPanel.jsx
// Task 11 / GOV-7 — Business Web field-service (Movers) booking panel. Drops into the existing Scheduling
// surface. Renders ONLY when the BACKEND eligibility gate says so (no industry-name/tenant checks). Shows
// generic service-job fields (pickup/destination/consent/status/ETA) customer-safely; ETA is truthful
// (AVAILABLE/NO_DATA/UNAVAILABLE/STALE/ERROR — never "0 min", never provider internals).
//
// GOV-7 WS2-B (this file, ADDITIVE): crew NAME-assignment, optional vehicle/resource, backend-authoritative
// workflow transitions (no optimistic status mutation — 422 surfaced), truthful ETA refresh, and a truthful
// tracking-state indicator (active/stale/ended + "customer notified" derived from state; the opaque token is
// NEVER exposed to the business UI). The BACKEND is the sole workflow authority; every mutation is a call to
// the deployed WS2-A endpoint and the panel re-reads from the backend response — it never invents state.

import React, { useEffect, useMemo, useState } from 'react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Backend-driven eligibility hook — the ONLY authority the surface trusts (FRONTEND_INDUSTRY_NAME_AUTH_BRANCHES=0).
export function useServiceWorkflowEligibility(api) {
  const [state, setState] = useState({ loading: true, eligibility: null });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const resp = await api.get(`${BACKEND_URL}/api/v1/me/service-workflow-eligibility`);
        if (alive) setState({ loading: false, eligibility: resp.data || null });
      } catch (e) {
        if (alive) setState({ loading: false, eligibility: null }); // fail-closed: no workflow shown
      }
    })();
    return () => { alive = false; };
  }, [api]);
  return state;
}

const ETA_LABEL = {
  available: null, // handled specially (shows minutes)
  no_data: 'ETA not available yet',
  unavailable: 'Live ETA is temporarily unavailable',
  stale: 'ETA is stale — refresh needed',
  error: "Couldn't calculate an ETA right now",
};

// Terminal appointment states — assign/transition/tracking-active controls are hidden once here.
const TERMINAL_STATES = new Set(['completed', 'cancelled', 'canceled', 'declined', 'no_show', 'expired', 'failed']);

// Human labels for the generic multi-stop service workflow. IDs/enums are NEVER shown raw to the user.
const STATUS_HUMAN = {
  assigned: 'Assigned',
  preparing: 'Preparing',
  en_route: 'En Route',
  en_route_pickup: 'En Route to Pickup',
  arrived_pickup: 'Arrived at Pickup',
  arrived: 'Arrived',
  in_service: 'In Service',
  en_route_destination: 'En Route to Destination',
  arrived_destination: 'Arrived at Destination',
  completed: 'Completed',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  declined: 'Declined',
  no_show: 'No Show',
  expired: 'Expired',
  failed: 'Failed',
};

// enum → human label (never render the raw enum/id). Falls back to Title Case for unknown states.
export function humanizeStatus(s) {
  if (!s) return '—';
  if (STATUS_HUMAN[s]) return STATUS_HUMAN[s];
  return String(s).split('_').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');
}

// Compute the valid NEXT state(s) from the current status + the profile's ordered status_workflow.
// The backend remains the sole authority; this only decides which transition buttons to OFFER. Impossible
// transitions are simply never rendered.
function nextValidStates(currentStatus, workflow) {
  if (!Array.isArray(workflow) || workflow.length === 0) return [];
  const idx = workflow.indexOf(currentStatus);
  if (idx < 0) return [];                 // out-of-workflow → offer nothing
  const next = workflow[idx + 1];
  return next ? [next] : [];
}

function fmtTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
}

function EtaState({ etaState, etaAvailable, ended }) {
  // etaState = effective eta snapshot (customer-safe). etaAvailable = backend capability flag.
  const s = etaState || {};
  const state = s.eta_state;
  const lastUpdated = s.last_updated_at || s.updated_at || s.estimated_arrival_ts || s.estimated_arrival_at;
  // A job whose tracking has ENDED has no active travel leg by design, so the backend correctly reports
  // eta_state="unavailable" (reason no_active_travel_leg). Rendering the generic "temporarily unavailable"
  // copy there reads as a malfunction — nothing is broken and nothing will arrive later. Show a neutral
  // dash and let "Tracking: ended" + "Final: Completed" carry the meaning. Non-terminal states are
  // untouched: an active travel leg still shows minutes, stale still says stale.
  if (ended) {
    return <span className="text-gray-500" data-testid="eta-ended">—</span>;
  }
  if (!etaAvailable) {
    return <span className="text-amber-700" data-testid="eta-unavailable">ETA not available (routing provider not configured)</span>;
  }
  if (state === 'available' && s.duration_seconds != null) {
    const mins = Math.round(s.duration_seconds / 60);
    return <span data-testid="eta-available">~{mins} min{mins === 1 ? '' : 's'}</span>;
  }
  if (state === 'stale') {
    // Truthful: never present a stale value as live — show it IS stale + when it was last known.
    return (
      <span className="text-gray-600" data-testid="eta-stale">
        {ETA_LABEL.stale}{lastUpdated ? ` (last updated ${fmtTs(lastUpdated)})` : ''}
      </span>
    );
  }
  if (state && ETA_LABEL[state] !== undefined && state !== 'available') {
    return <span className="text-gray-600" data-testid={`eta-${state}`}>{ETA_LABEL[state]}</span>;
  }
  return <span className="text-gray-500" data-testid="eta-none">—</span>; // unknown ≠ 0
}

// Resolve assigned crew to NAMES only. Never renders a raw staff/crew id. Order of preference:
//   1) names the assignment response already carries (backend is authoritative)
//   2) ids mapped through the tenant roster to names
//   3) neutral "N assigned" count (still never a raw id)
function crewNames(crewObj, roster) {
  const c = crewObj || {};
  if (Array.isArray(c.members) && c.members.length) {
    const names = c.members.map((m) => m.name || m.display_name).filter(Boolean);
    if (names.length) return { names, count: c.members.length };
  }
  if (Array.isArray(c.crew_display_names) && c.crew_display_names.length) {
    return { names: c.crew_display_names.filter(Boolean), count: c.crew_display_names.length };
  }
  const ids = Array.isArray(c.crew_staff_ids) ? c.crew_staff_ids : [];
  if (ids.length) {
    const byId = new Map((roster || []).map((r) => [r.id, r.name || r.email]));
    const names = ids.map((id) => byId.get(id)).filter(Boolean);
    if (names.length) return { names, count: ids.length };
    return { names: [], count: ids.length }; // ids present but unresolved → count only, NEVER the raw id
  }
  return { names: [], count: 0 };
}

const MoversJobPanel = ({
  appointment,
  eligibility,
  api,                 // { get, put, post } → each resolves { ok, status, data }. Absent → read-only panel.
  staffRoster,         // [{ id, name, role }] from the tenant roster (getBusinessStaff) — NAME selector source.
  onChanged,           // called after a successful backend mutation so the parent re-reads authoritative detail.
  businessDisplayName, // resolved business NAME (never a raw id).
}) => {
  // Render nothing unless the backend gate enables the profile-driven workflow for this tenant+role.
  const a = appointment || {};
  const roster = Array.isArray(staffRoster) ? staffRoster : [];

  // ---- assignment (crew NAME multi-select + optional vehicle/resource) ----
  const [selectedCrew, setSelectedCrew] = useState([]);
  const [vehicle, setVehicle] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignErr, setAssignErr] = useState(null);
  const [assignment, setAssignment] = useState(null); // backend assignment response (crew NAMES + vehicle label)
  // GOV-7 canonical vehicle assignment + safe live-source status (backend-authoritative; names only).
  const [vehicleId, setVehicleId] = useState('');
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [srcStatus, setSrcStatus] = useState(null);
  const canManageVehicles = !!eligibility?.vehicle_management_allowed;

  useEffect(() => {
    let alive = true;
    if (!api || !eligibility?.vehicle_management_allowed) return undefined;
    (async () => {
      try { const r = await api.get(`${BACKEND_URL}/api/v1/fleet/vehicles`); if (alive && r?.ok) setFleetVehicles(r.data?.vehicles || []); } catch (e) { /* fail closed */ }
    })();
    return () => { alive = false; };
  }, [api, eligibility]);

  useEffect(() => {
    let alive = true;
    const apptId = (appointment || {}).appointment_id;
    if (!api || !apptId) return undefined;
    (async () => {
      try { const r = await api.get(`${BACKEND_URL}/api/v1/scheduling/appointments/${encodeURIComponent(apptId)}/source-status`); if (alive && r?.ok) setSrcStatus(r.data || null); } catch (e) { /* fail closed */ }
    })();
    return () => { alive = false; };
  }, [api, appointment]);

  // ---- workflow transition (backend-authoritative) ----
  const [transitioning, setTransitioning] = useState(null); // the state currently being requested
  const [transitionErr, setTransitionErr] = useState(null);

  // ---- ETA (truthful refresh) ----
  const [refreshedEta, setRefreshedEta] = useState(null);
  const [etaLoading, setEtaLoading] = useState(false);

  const status = a.status;
  const terminal = TERMINAL_STATES.has(status);
  const workflow = eligibility?.status_workflow || [];
  const nexts = useMemo(() => (terminal ? [] : nextValidStates(status, workflow)), [terminal, status, workflow]);

  // Effective crew/vehicle = latest backend assignment response, else the appointment snapshot.
  const effCrew = crewNames(assignment?.crew || assignment || a.crew, roster);
  const vehicleLabel = assignment?.vehicle_resource_label || assignment?.vehicle?.label || a.vehicle_resource_label || a.vehicle?.label || null;

  // Effective ETA = latest refresh (backend) else the appointment snapshot. Both come from the backend.
  const effEta = refreshedEta || a.eta_state;

  // Tracking state (derived — the opaque token is NEVER surfaced to the business UI).
  const tracking = a.tracking || {};
  const trackingState = tracking.tracking_state || (tracking.tracking_ended ? 'ended' : tracking.state);
  const trackingEnded = terminal || tracking.tracking_ended === true || trackingState === 'ended';
  const customerNotified = tracking.customer_notified === true || tracking.link_sent === true || !!tracking.notified_at;

  if (!eligibility || !eligibility.workflow_enabled) return null;

  const loc = (x) => (x && (x.address || (x.latitude != null ? `${x.role || 'location'}` : null))) || '—';

  const toggleCrew = (id) => setSelectedCrew((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const submitAssignment = async () => {
    if (!api || !a.appointment_id) return;
    setAssigning(true); setAssignErr(null);
    try {
      const body = { crew_staff_ids: selectedCrew };  // ORDER preserved — drives phone-source precedence
      if (vehicle.trim()) body.vehicle_resource_id = vehicle.trim();   // legacy free-text (non-authoritative)
      if (vehicleId) body.vehicle_id = vehicleId;                      // GOV-7 canonical tenant vehicle authority
      const resp = await api.put(`${BACKEND_URL}/api/v1/scheduling/appointments/${encodeURIComponent(a.appointment_id)}/assignment`, body);
      if (!resp || !resp.ok) {
        setAssignErr((resp && resp.data && (resp.data.detail || resp.data.message)) || `Assignment failed (HTTP ${resp?.status || '?'})`);
        return; // do NOT mutate local state on failure
      }
      setAssignment(resp.data || null); // backend-authoritative (crew NAMES + vehicle label)
      if (onChanged) await onChanged();
    } catch (e) {
      setAssignErr(e.message || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const doTransition = async (nextStatus) => {
    if (!api || !a.appointment_id) return;
    setTransitioning(nextStatus); setTransitionErr(null);
    try {
      const resp = await api.put(`${BACKEND_URL}/api/v1/scheduling/appointments/${encodeURIComponent(a.appointment_id)}/status`, { next_status: nextStatus });
      if (!resp || !resp.ok) {
        // 422 (invalid/backward/out-of-workflow) — surface the reason, DO NOT change state.
        setTransitionErr((resp && resp.data && (resp.data.detail || resp.data.message)) || `Transition rejected (HTTP ${resp?.status || '?'})`);
        return;
      }
      // Success: re-read authoritative detail from the backend. NO optimistic status mutation here.
      if (onChanged) await onChanged();
    } catch (e) {
      setTransitionErr(e.message || 'Transition failed');
    } finally {
      setTransitioning(null);
    }
  };

  const refreshEta = async () => {
    if (!api || !a.appointment_id) return;
    setEtaLoading(true);
    try {
      const resp = await api.post(`${BACKEND_URL}/api/v1/scheduling/appointments/${encodeURIComponent(a.appointment_id)}/eta/refresh`, {});
      if (resp && resp.ok) setRefreshedEta(resp.data || null); // backend-computed; NO client ETA math
    } catch (e) {
      /* leave prior truthful state; never fabricate an ETA */
    } finally {
      setEtaLoading(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t space-y-2 text-sm" data-testid="movers-job-panel">
      <div className="flex items-center gap-2">
        <Badge variant="outline">Field-service job</Badge>
        <Badge variant={terminal ? 'outline' : 'default'} data-testid="job-status">{humanizeStatus(status)}</Badge>
        {businessDisplayName && <span className="text-xs text-gray-400" data-testid="job-business">{businessDisplayName}</span>}
      </div>
      <div>Pickup: <b data-testid="job-pickup">{loc(a.service_location)}</b></div>
      <div>Destination: <b data-testid="job-destination">{loc(a.destination_location)}</b></div>
      <div>Consent: <b data-testid="job-consent">{a.location_consent?.granted ? 'granted' : (eligibility.consent_required ? 'required' : 'n/a')}</b></div>

      {/* Assigned crew — NAMES only (never a raw id) */}
      {(effCrew.names.length > 0 || effCrew.count > 0) && (
        <div data-testid="assigned-crew">
          Crew: <b>{effCrew.names.length > 0 ? effCrew.names.join(', ') : `${effCrew.count} assigned`}</b>
        </div>
      )}
      {vehicleLabel && <div data-testid="assigned-vehicle">Vehicle: <b>{vehicleLabel}</b></div>}

      {/* ETA — truthful; refresh calls the backend, never computes client-side */}
      <div className="flex items-center gap-2">
        <span>ETA: <b><EtaState etaState={effEta} etaAvailable={eligibility.location_eta_available} ended={trackingEnded} /></b></span>
        {!terminal && eligibility.location_eta_available && api && (
          <Button size="sm" variant="outline" data-testid="eta-refresh" onClick={refreshEta} disabled={etaLoading}>
            {etaLoading ? 'Refreshing…' : 'Refresh ETA'}
          </Button>
        )}
      </div>

      {/* Tracking state — active / stale / ended + customer-notified indicator. Token is NEVER shown. */}
      <div className="text-xs" data-testid="tracking-indicator">
        Tracking:{' '}
        {trackingEnded ? (
          <b data-testid="tracking-ended" className="text-gray-500">ended</b>
        ) : trackingState === 'stale' ? (
          <b data-testid="tracking-stale" className="text-amber-700">stale{tracking.last_updated_at ? ` (last updated ${fmtTs(tracking.last_updated_at)})` : ''}</b>
        ) : (
          <b data-testid="tracking-active" className="text-green-700">active</b>
        )}
        {!trackingEnded && customerNotified && (
          <span data-testid="tracking-notified" className="ml-2 text-gray-500">· customer notified (tracking link sent)</span>
        )}
        {srcStatus && srcStatus.active_location_source_label ? (
          <div data-testid="source-status" className="mt-1 text-gray-600">
            {/* Once tracking has ended there IS no active location source, so the backend's "No fresh
                location" label would read as a fault rather than a completed job. Neutral dash instead;
                the operator name / revoked hint are live-tracking detail and are dropped with it. */}
            Live location source: <b>{trackingEnded ? '—' : srcStatus.active_location_source_label}</b>
            {!trackingEnded && srcStatus.active_location_operator_name ? ` — ${srcStatus.active_location_operator_name}` : ''}
            {!trackingEnded && srcStatus.tracker_health_safe === 'revoked' ? ' (revoked)' : ''}
          </div>
        ) : null}
      </div>

      {/* Terminal jobs: hide assign/transition/tracking-active controls; show the final state only. */}
      {terminal ? (
        <div className="pt-1" data-testid="terminal-state">
          <Badge variant="outline">Final: {humanizeStatus(status)}</Badge>
        </div>
      ) : api ? (
        <>
          {/* Crew assignment — NAME-based multi-select from the tenant roster (never renders a raw id) */}
          <div className="pt-2 border-t" data-testid="crew-assign">
            <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Assign crew</div>
            {roster.length === 0 ? (
              <p className="text-xs text-gray-400">No staff in the tenant roster.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="crew-select">
                {roster.map((s) => (
                  <label key={s.id} className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCrew.includes(s.id)}
                      onChange={() => toggleCrew(s.id)}
                      aria-label={s.name || s.email}
                    />
                    {s.role ? `${s.name} (${s.role})` : s.name}
                  </label>
                ))}
              </div>
            )}
            {canManageVehicles && (
              <div className="mt-2">
                <label className="text-xs text-gray-500">Assigned vehicle</label>
                <select className="mt-1 h-8 block border rounded" data-testid="vehicle-select" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                  <option value="">(no vehicle)</option>
                  {fleetVehicles.filter((v) => v.status === 'active').map((v) => (
                    <option key={v.vehicle_id} value={v.vehicle_id}>{v.display_name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="mt-2">
              <label className="text-xs text-gray-500">Vehicle label (legacy, optional)</label>
              <Input className="mt-1 h-8" data-testid="vehicle-input" value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="e.g. Truck 12" />
            </div>
            {assignErr && <p className="text-xs text-red-600 mt-1" data-testid="assign-error">{assignErr}</p>}
            <Button size="sm" className="mt-2" data-testid="assign-submit" onClick={submitAssignment} disabled={assigning || selectedCrew.length === 0}>
              {assigning ? 'Assigning…' : 'Assign crew'}
            </Button>
          </div>

          {/* Workflow transitions — ONLY valid next state(s); backend is the sole authority; 422 is surfaced */}
          <div className="pt-2 border-t" data-testid="transitions">
            <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Advance job</div>
            {nexts.length === 0 ? (
              <p className="text-xs text-gray-400" data-testid="no-transitions">No further steps available.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {nexts.map((ns) => (
                  <Button
                    key={ns}
                    size="sm"
                    data-testid={`transition-${ns}`}
                    onClick={() => doTransition(ns)}
                    disabled={transitioning != null}
                  >
                    {transitioning === ns ? 'Working…' : humanizeStatus(ns)}
                  </Button>
                ))}
              </div>
            )}
            {transitionErr && <p className="text-xs text-red-600 mt-1" data-testid="transition-error">{transitionErr}</p>}
          </div>
        </>
      ) : null}

      <div className="text-xs text-gray-400">
        Workflow: {(workflow || []).map(humanizeStatus).join(' → ') || '—'}
      </div>
    </div>
  );
};

export default MoversJobPanel;
